import React, { useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import './mobile.css';
import MobileHeader from './MobileHeader';
import { useAuth } from '../contexts/AuthContext';
import ScrollToTop from '../components/ScrollToTop';

const tabs = [
  { key:'courses',     label:'Courses',    icon:'📚', path:'/m/courses' },
  { key:'events',      label:'Events',     icon:'📆', path:'/m/events' },
  { key:'assignments', label:'Assignments',icon:'📝', path:'/m/assignments' },
  { key:'chat',        label:'AI Chat',    icon:'/logos/blue_gold_head.png', isImage: true, path:'/m/chat' },
  { key:'documents',   label:'Documents',  icon:'📄', path:'/m/documents' }
];

export default function MobileShell(props){
  const loc = useLocation();
  const { accessToken, user } = useAuth();
  const isAuthenticated = !!accessToken || !!user;

  useEffect(() => {
    document.body.classList.add('mobile-mode');
    return () => document.body.classList.remove('mobile-mode');
  }, []);

  useEffect(() => {
    // Disable browser scroll restoration for mobile navigation
    if ('scrollRestoration' in window.history) {
      console.log('[MobileShell] Setting scrollRestoration to manual');
      window.history.scrollRestoration = 'manual';
      console.log('[MobileShell] scrollRestoration is now:', window.history.scrollRestoration);
    } else {
      console.log('[MobileShell] scrollRestoration not supported');
    }
  }, []);

  useEffect(() => {
    // persistent hide: immediately remove existing buttons and watch for newly-inserted ones
    const MOBILE_MAX = 820;
    function shouldApply() { return window.innerWidth <= MOBILE_MAX; }

    function hideMatching(element) {
      if (!element) return;

      // Only hide likely action elements to avoid removing unrelated content
      const isActionEl = (
        ['BUTTON','A'].includes(element.tagName) ||
        (element.getAttribute && element.getAttribute('role') === 'button') ||
        element.classList.contains('btn') ||
        element.classList.contains('m-top-btn')
      );
      if (!isActionEl) return;

      const txt = (element.textContent || '').trim();
      const aria = (element.getAttribute && (element.getAttribute('aria-label') || element.getAttribute('aria-labelledby') || '')) || '';
      const title = (element.getAttribute && element.getAttribute('title')) || '';
      const combined = (txt + ' ' + aria + ' ' + title).toLowerCase();

      // Match add/create/edit words near the start or as standalone actions
      const shouldHide = /(^|\W)(add|create|edit)\b/.test(combined);
      if (shouldHide) {
        if (element.dataset && element.dataset._origDisplay) return; // already hidden
        element.dataset._origDisplay = element.style.display || '';
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.style.pointerEvents = 'none';
      }
    }

    function scanAndHide() {
      if (!shouldApply()) return;
      const els = Array.from(document.querySelectorAll('button, a, div'));
      els.forEach(hideMatching);
    }

    // initial pass
    scanAndHide();

    // observe DOM mutations so newly-rendered buttons are hidden
    const observer = new MutationObserver(mutations => {
      if (!shouldApply()) return;
      mutations.forEach(m => {
        if (m.addedNodes && m.addedNodes.length) {
          m.addedNodes.forEach(node => {
            if (!(node instanceof Element)) return;
            // check node itself and its subtree
            hideMatching(node);
            node.querySelectorAll && node.querySelectorAll('button, a, div').forEach(hideMatching);
          });
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: false });

    // also re-run on resize in case viewport flips to desktop/mobile
    function onResize() {
      if (shouldApply()) scanAndHide();
      else {
        // restore previously-hidden elements when no longer on mobile
        const hidden = Array.from(document.querySelectorAll('[data-_orig-display]'));
        hidden.forEach(el => {
          el.style.display = el.dataset._origDisplay || '';
          el.style.visibility = '';
          el.style.pointerEvents = '';
          delete el.dataset._origDisplay;
        });
      }
    }
    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="m-app">
      {/* Reset scroll position on every route change */}
      <ScrollToTop />
      {/* Show the global MobileHeader on all mobile pages (including the splash) */}
      <MobileHeader />
      <div className="m-content">
        <Outlet />
      </div>
      {isAuthenticated && (
        <nav className="m-tabbar" role="tablist" aria-label="Mobile navigation">
          {tabs.map(t=>{
            const active = loc.pathname.startsWith(t.path);
            return (
              <Link key={t.key} to={t.path} className={`m-tab ${active ? 'active':''}`} aria-selected={active} role="tab" aria-label={t.label}>
                {t.isImage ? (
                  <img src={t.icon} alt={t.label} className="m-icon-img" />
                ) : (
                  <span className="m-icon">{t.icon}</span>
                )}
                <span className="m-label">{t.label}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}